import axios from 'axios';
import dotenv from 'dotenv';
import CookieKeys from '@/model/constants/cookieKeys';
import { APP_VERSION, UPDATED_FROM_KEY } from '@/utils/appVersion';

dotenv.config();

/** Paylaşılan axios (SET_BEARER) ile karışmasın; tek Authorization header. */
const telemetryAxios = axios.create({
  baseURL: process.env.VUE_APP_ROOT_API,
  timeout: 15000
});

telemetryAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem(CookieKeys.AUTH_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.skipAuthRefresh = true;
  return config;
});

/** Backend SelfServiceSessionEventType enum sırası (sayısal). */
export const SelfServiceEventType = {
  CartLineAdded: 0,
  CartLineUpdated: 1,
  CartLineRemoved: 2,
  CartCreated: 3,
  CartRemoved: 4,
  CartSwitched: 5,
  PaymentFlowCancelled: 6,
  MobileOrderCancelled: 7,
  IntegrationOrderCancelled: 8,
  ActivePersonalChanged: 9,
  QrPaymentStarted: 10,
  DiscountApplied: 11,
  // Aşağıdakiler yalnızca süper admin "Kasa Teşhis Günlüğü"nde görünür.
  PosSaleRequested: 12,
  PosSaleSucceeded: 13,
  PosSaleFailed: 14,
  PosPaymentManuallyConfirmed: 15,
  OrderCloseFailed: 16,
  ScreenLocked: 17,
  ScreenUnlocked: 18,
  SessionStarted: 19,
  ClientError: 20,
  PosConnectFailed: 21,
  PriceValidationBlocked: 22
};

/** Aynı hatanın döngüde yüzlerce kez loglanmasını engeller. */
const MAX_CLIENT_ERRORS_PER_SESSION = 30;
let clientErrorCount = 0;

const QUEUE = [];
const DEBOUNCE_MS = 400;
const MAX_BATCH = 50;
const MAX_QUEUE = 400;
let flushTimer = null;
let storeRef = null;
let installed = false;

export function summarizeCartEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const out = {
    id: entry.id,
    productId: entry.productId,
    childProductId: entry.childProductId,
    quantity: entry.quantity,
    price: entry.price
  };
  if (entry.productName) {
    out.productName = String(entry.productName);
  }
  if (entry.extras && entry.extras.length) {
    const lines = [];
    entry.extras.forEach((extra) => {
      if (extra.variants && extra.variants.length) {
        extra.variants.forEach((v) => {
          if (v.quantity > 0) {
            const en = extra.name || 'Ekstra';
            const vn = v.name || '';
            lines.push(vn ? `${en} — ${vn} (${v.quantity}x)` : `${en} (${v.quantity}x)`);
          }
        });
      } else if (extra.name) {
        const q = extra.quantity != null ? extra.quantity : 1;
        if (q > 0) {
          lines.push(`${extra.name} (${q}x)`);
        }
      }
    });
    if (lines.length) out.extraLines = lines;
  }
  return out;
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, DEBOUNCE_MS);
}

function pushEvent(eventType, payloadObj) {
  if (!storeRef || !storeRef.state || !storeRef.state.selfservice) return;
  const clientSessionId = storeRef.state.selfservice.clientSessionId;
  if (!clientSessionId) return;

  const payloadJson =
    payloadObj === undefined || payloadObj === null
      ? null
      : typeof payloadObj === 'string'
        ? payloadObj
        : JSON.stringify(payloadObj);

  // UserId sunucuda oturum açan hesaptır; PIN ile aktif olan personeli ayrıca gönderiyoruz.
  const activePersonal = storeRef.state.selfservice.activePersonal;
  QUEUE.push({
    eventType,
    payloadJson,
    clientTimestamp: new Date().toISOString(),
    orderId: null,
    personalId: activePersonal && activePersonal.id ? Number(activePersonal.id) : null
  });
  if (QUEUE.length > MAX_QUEUE) {
    QUEUE.splice(0, QUEUE.length - MAX_QUEUE);
  }
  scheduleFlush();
}

async function flushQueue() {
  if (!storeRef || QUEUE.length === 0) return;
  const clientSessionId = storeRef.state.selfservice && storeRef.state.selfservice.clientSessionId;
  if (!clientSessionId) return;

  const token = localStorage.getItem(CookieKeys.AUTH_KEY);
  if (!token) return;

  const batch = QUEUE.splice(0, MAX_BATCH);
  const body = { clientSessionId, events: batch };

  try {
    await telemetryAxios.post('SelfService/session-events', body, {
      skipAuthRefresh: true
    });
  } catch {
    if (QUEUE.length + batch.length <= MAX_QUEUE) {
      QUEUE.unshift(...batch);
    }
  }

  if (QUEUE.length > 0) scheduleFlush();
}

/**
 * Sepet dışı olaylar (ödeme iptal, mobil iptal vb.)
 */
export function logSelfServiceManualEvent(eventType, payloadObj) {
  pushEvent(eventType, payloadObj);
}

function onMutation(mutation) {
  if (!mutation.type || !mutation.type.startsWith('selfservice/')) return;

  switch (mutation.type) {
    case 'selfservice/ADD_TO_CART': {
      const s = summarizeCartEntry(mutation.payload);
      pushEvent(SelfServiceEventType.CartLineAdded, s);
      break;
    }
    case 'selfservice/UPDATE_TO_CART': {
      const p = mutation.payload;
      const entry = p && p.entry ? summarizeCartEntry(p.entry) : null;
      pushEvent(SelfServiceEventType.CartLineUpdated, { index: p && p.index, entry });
      break;
    }
    case 'selfservice/REMOVE_ENTRY_FROM_CART': {
      const p = mutation.payload;
      const entry = p && p.entry != null ? p.entry : p;
      const s = summarizeCartEntry(entry);
      if (s && p && p.removeReason) {
        const rr = String(p.removeReason).trim();
        if (rr) s.removeReason = rr;
      }
      pushEvent(SelfServiceEventType.CartLineRemoved, s);
      break;
    }
    case 'selfservice/REMOVE_THE_CART': {
      if (window._isOrderCompleted) break;
      const session = storeRef.state.selfservice.paymentSessionState;
      const hasPayments = session && session.orderPayments && session.orderPayments.length > 0;
      const hasDiscounts = session && session.discounts && session.discounts.length > 0;

      if (hasPayments || hasDiscounts) {
        const activeCart = storeRef.state.selfservice.carts.find(c => c.active);
        const totalPrice = activeCart ? activeCart.totalPrice : 0;
        const paymentsStr = session.orderPayments.map(p => `${p.type || 'Ödeme'}: ${p.payedPrice} ₺`).join(', ');
        const discountsStr = session.discounts.map(d => `${d.description || 'İndirim'}: ${d.payedPrice} ₺`).join(', ');

        const payload = {
          "Durum": "Sepet iptal edildi (Ödemeler/indirimler silindi)",
          "Toplam Tutar": totalPrice + " ₺"
        };
        if (paymentsStr) payload["Ödemeler"] = paymentsStr;
        if (discountsStr) payload["İndirimler"] = discountsStr;

        pushEvent(SelfServiceEventType.PaymentFlowCancelled, payload);
      }
      break;
    }
    case 'selfservice/SET_CURRENT_CART': {
      const v = mutation.payload;
      pushEvent(SelfServiceEventType.CartSwitched, v ? { cartId: v.id } : {});
      break;
    }
    case 'selfservice/SET_CURRENT_CART_CUSTOMER': {
      const c = mutation.payload;
      if (c) {
        pushEvent(SelfServiceEventType.QrPaymentStarted, { customerId: c.id, customerName: c.name });
      }
      break;
    }
    case 'selfservice/SET_ACTIVE_PERSONAL': {
      // Kullanıcı kodu (PIN) bilerek gönderilmez.
      const p = mutation.payload;
      pushEvent(SelfServiceEventType.ActivePersonalChanged, p
        ? { locked: false, personalId: p.id, name: [p.name, p.surname].filter(Boolean).join(' ') }
        : { locked: true });
      break;
    }
    default:
      break;
  }
}

function errorText(value) {
  if (!value) return '';
  if (value instanceof Error) {
    // Safari'de stack mesajı içermez ("fn@url:satır"); mesajı başa ekle.
    const stack = value.stack || '';
    const message = value.message || String(value);
    return stack.indexOf(message) >= 0 ? stack : `${message}\n${stack}`;
  }
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

/** Kullanıcıyı etkilemeyen, bilinen zararsız hatalar (canlı teşhis günlüğü 19-21.10 analizi):
 * vuesax tablo/popup bileşenlerinin kapanırken boyut hesaplaması, aynı sayfaya tekrar yönlenme,
 * vee-validate'in kaldırılmış alanı doğrulama uyarısı. Günlüğü gerçek hatalardan uzaklaştırıyordu. */
const IGNORED_CLIENT_ERRORS = [
  /changeHeight|scrollHeight/,
  /changeTdsWidth|listenerChangeWidth/,
  /NavigationDuplicated|Avoided redundant navigation/,
  /\[vee-validate\] Validating a non-existent field/
];

function isOnSelfServiceScreen() {
  return typeof window !== 'undefined' && window.location.pathname.toLowerCase().indexOf('/pages/selfservice') === 0;
}

/** Kasa ekranındaki yakalanmamış JS hataları — "ödeme göründü ama sipariş kapanmadı" gibi
 * şikâyetlerde ekranın arka planda hata verip vermediğini görmek için. Dinleyiciler kasadan
 * çıkıldıktan sonra da açık kaldığı için yalnızca kasa ekranındaki hatalar yazılır. */
export function logSelfServiceClientError(source, error, extra) {
  if (clientErrorCount >= MAX_CLIENT_ERRORS_PER_SESSION) return;
  if (!isOnSelfServiceScreen()) return;
  const message = errorText(error);
  if (IGNORED_CLIENT_ERRORS.some((re) => re.test(message))) return;
  clientErrorCount++;
  const payload = Object.assign({ source, message: message.slice(0, 1500), page: window.location.pathname }, extra || {});
  pushEvent(SelfServiceEventType.ClientError, payload);
}

/** Teşhis için kaba cihaz türü (POS köprüsü yalnızca Windows kasada çalışır). */
export function detectDeviceType() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPad|iPhone/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)) return 'iPad/iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh/.test(ua)) return 'Mac';
  return 'Diğer';
}

function onWindowError(event) {
  logSelfServiceClientError('window.onerror', event && (event.error || event.message), {
    file: event && event.filename ? String(event.filename).split('/').pop() : undefined,
    line: event && event.lineno
  });
}

function onUnhandledRejection(event) {
  logSelfServiceClientError('unhandledrejection', event && event.reason);
}

export function installSelfServiceSessionTelemetry(store) {
  if (installed) return;
  storeRef = store;
  installed = true;
  store.subscribe(onMutation);
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  let updatedFrom;
  try {
    const raw = sessionStorage.getItem(UPDATED_FROM_KEY);
    if (raw) {
      updatedFrom = JSON.parse(raw).from;
      sessionStorage.removeItem(UPDATED_FROM_KEY);
    }
  } catch (e) {
    updatedFrom = undefined;
  }
  pushEvent(SelfServiceEventType.SessionStarted, {
    // Hangi kasanın hangi sürümle çalıştığı — yayından sonra eski sürümde kalan kasaları görmek için.
    appVersion: APP_VERSION,
    updatedFrom,
    // Sayfa yenilenmiş mi (F5) yoksa ilk açılış mı — yarım kalan ödemeleri yorumlamak için önemli.
    navigation: (() => {
      try {
        const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        return nav ? nav.type : undefined;
      } catch (e) {
        return undefined;
      }
    })(),
    device: detectDeviceType(),
    userAgent: navigator.userAgent,
    online: navigator.onLine
  });
}

export function ensureSelfServiceClientSessionId(store) {
  if (!store.state.selfservice) return;
  if (store.state.selfservice.clientSessionId) return;
  let id;
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    id = crypto.randomUUID();
  } else {
    id = `ss-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
  store.commit('selfservice/SET_CLIENT_SESSION_ID', id);
}
